"use client";

import { useState, useMemo, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import {
  ROLES_TREE,
  LOCATIONS_TREE,
  ROLE_CATEGORIES,
  findLabelById,
  getLocationsForSeasonGroup,
  getAllLeafIdsFromTree,
} from "@/lib/taxonomy";
import { CascadingTree } from "@/app/components/CascadingTree";
import { SEASON_TYPES } from "@/lib/seasons";

import { sendFirstMessageToEmployer } from "@/app/actions/messaging";
import type { UserRole } from "@/lib/auth";
import { VerifiedBadge } from "@/app/components/VerifiedBadge";
import { buildWorkSignupHref } from "@/lib/signup-next";

// ═══ TYPES ═══

interface BrowseCompany {
  id: string;
  name: string;
  slug: string | null;
  logo_url: string | null;
  description: string | null;
  location: string | null;
  company_size: string | null;
  hiring_regions: string[] | null;
  hiring_roles: string[] | null;
  hiring_season_types: string[] | null;
  allow_inbound_messages: boolean;
  brand_color: string | null;
  response_time: string | null;
  verified: boolean;
  avg_response_hours: number | null;
  activeListingCount: number;
  activeThisMonth: boolean;
  recencyBucket: "week" | "month" | null;
}

interface CandidateProfile {
  id: string;
  availabilityWindows: {
    id: string;
    season_label: string;
    positions: string[];
    preferred_locations: string[];
    status: string;
  }[];
}

interface ConversationEntry {
  id: string;
  status: string;
  windowId: string | null;
}

const SEASON_TYPE_LABELS: Record<string, string> = {
  // Broad IDs (new format)
  "winter": "Winter",
  "summer": "Summer",
  "festivals": "Festivals",
  // Legacy region-specific IDs
  "alpine-winter": "Winter",
  "na-winter": "Winter",
  "japan-winter": "Winter",
  "southern-winter": "Winter",
  "med-summer": "Summer",
  "caribbean": "Summer",
  "aus-summer": "Summer",
  "se-asia": "Summer",
  "na-summer": "Summer",
  "middle-east": "Summer",
  "uk-festivals": "Festivals",
  "eu-festivals": "Festivals",
  "na-festivals": "Festivals",
  "aus-festivals": "Festivals",
};

const SEASON_TYPE_COLORS: Record<string, { bg: string; tint: string }> = {
  winter: { bg: "#1B2A4A", tint: "#E8EBF0" },
  summer: { bg: "#0EA5E9", tint: "#E8F7FE" },
  festivals: { bg: "#E8835C", tint: "#FDF0EB" },
};

function getSeasonDisplayLabels(seasonTypes: string[] | null): { label: string; bg: string; tint: string }[] {
  if (!seasonTypes || seasonTypes.length === 0) return [];
  const seen = new Set<string>();
  const result: { label: string; bg: string; tint: string }[] = [];
  for (const id of seasonTypes) {
    const label = SEASON_TYPE_LABELS[id];
    if (!label || seen.has(label)) continue;
    seen.add(label);
    const colors = SEASON_TYPE_COLORS[label.toLowerCase()] ?? { bg: "#1B2840", tint: "#E8EBF0" };
    result.push({ label, ...colors });
  }
  return result;
}

const SEASON_GROUP_IDS: Record<string, string[]> = {
  snow: ["winter", "alpine-winter", "na-winter", "japan-winter", "southern-winter"],
  sun: ["summer", "med-summer", "caribbean", "aus-summer", "se-asia", "na-summer", "middle-east"],
  festival: ["festivals", "uk-festivals", "eu-festivals", "na-festivals", "aus-festivals"],
};

const SEASON_GROUP_CHIPS = [
  { id: "snow", label: "Winter", icon: "\u2744\uFE0F" },
  { id: "sun", label: "Summer", icon: "\u2600\uFE0F" },
  { id: "festival", label: "Festivals", icon: "\uD83C\uDFAA" },
] as const;

// Maps ROLE_CATEGORIES keys to broad hiring_season_types values.
// Hospitality and agriculture are cross-season so we skip them.
const ROLE_CATEGORY_TO_SEASON: Record<string, string> = {
  ski: "winter",
  yacht: "summer",
  watersports: "summer",
  festivals: "festivals",
};

// Build reverse lookup: role leaf ID → season type
const _roleToSeason = new Map<string, string>();
for (const [cat, season] of Object.entries(ROLE_CATEGORY_TO_SEASON)) {
  for (const roleId of ROLE_CATEGORIES[cat] ?? []) {
    if (!_roleToSeason.has(roleId)) _roleToSeason.set(roleId, season);
  }
}

/** Infer broad season types from hiring roles when hiring_season_types is empty */
function inferSeasonTypes(roles: string[] | null): string[] {
  if (!roles || roles.length === 0) return [];
  const seasons = new Set<string>();
  for (const r of roles) {
    const s = _roleToSeason.get(r);
    if (s) seasons.add(s);
  }
  return Array.from(seasons);
}

interface EmployerFilters {
  seasonType: string;   // retained for backwards compat, always "" in new UI
  seasonGroup: string;  // "snow" | "sun" | "festival" | ""
  roles: Set<string>;
  locations: Set<string>;
  activeThisMonth: boolean;
}

function formatResponseTime(hours: number): string {
  if (hours <= 2) return "Usually responds within a few hours";
  if (hours <= 24) return "Usually responds within a day";
  return "Usually responds within a few days";
}

// ═══ FILTER SIDEBAR ═══

function FilterSidebar({
  filters,
  setFilters,
  totalCount,
  filteredCount,
  onClose,
}: {
  filters: EmployerFilters;
  setFilters: React.Dispatch<React.SetStateAction<EmployerFilters>>;
  totalCount: number;
  filteredCount: number;
  onClose?: () => void;
}) {
  const isMobile = !!onClose;

  const hasFilters =
    filters.seasonType !== "" ||
    filters.seasonGroup !== "" ||
    filters.roles.size > 0 ||
    filters.locations.size > 0 ||
    filters.activeThisMonth;

  function clearAll() {
    setFilters({
      seasonType: "",
      seasonGroup: "",
      roles: new Set(),
      locations: new Set(),
      activeThisMonth: false,
    });
  }


  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-start mb-1">
        <h3 className="font-serif text-xl">Find Employers</h3>
        {onClose && (
          <button
            onClick={onClose}
            className="md:hidden w-8 h-8 rounded-sm bg-warm-bg border-none text-lg cursor-pointer flex items-center justify-center"
            aria-label="Close filters"
          >
            &times;
          </button>
        )}
      </div>
      {!isMobile && (
        <p className="text-xs text-text-muted mb-5">
          {hasFilters
            ? `${filteredCount} compan${filteredCount !== 1 ? "ies" : "y"} matching`
            : `${totalCount} compan${totalCount !== 1 ? "ies" : "y"}`}
        </p>
      )}

      {hasFilters && (
        <button
          onClick={clearAll}
          className="w-full text-[11px] text-text-muted hover:text-gold transition-colors bg-transparent border-none cursor-pointer font-sans mb-4 text-right"
        >
          Clear all filters
        </button>
      )}

      <div className="flex-1 overflow-y-auto space-y-5 pb-4">
        {/* Roles */}
        <CascadingTree
          tree={ROLES_TREE}
          selected={filters.roles}
          onChange={(roles) => setFilters((f) => ({ ...f, roles }))}
          label="Roles"
          maxHeight="240px"
          searchable
        />

        {/* Locations */}
        <CascadingTree
          tree={filters.seasonGroup
            ? (getLocationsForSeasonGroup(filters.seasonGroup) ?? LOCATIONS_TREE)
            : LOCATIONS_TREE}
          selected={filters.locations}
          onChange={(locations) => setFilters((f) => ({ ...f, locations }))}
          label="Locations"
          maxHeight="240px"
          searchable
        />
      </div>

      {/* Mobile apply button */}
      {onClose && (
        <button
          onClick={onClose}
          className="md:hidden w-full py-3 rounded-sm text-[14px] font-semibold bg-gold text-white border-none cursor-pointer font-sans mt-4 hover:bg-gold-light transition-all"
        >
          Show {filteredCount} compan{filteredCount !== 1 ? "ies" : "y"}
        </button>
      )}
    </div>
  );
}

// ═══ MAIN COMPONENT ═══

export function EmployerBrowseContent({
  companies,
  userRole,
  candidateProfile,
  conversationMap,
}: {
  companies: BrowseCompany[];
  userRole?: UserRole;
  candidateProfile: CandidateProfile | null;
  conversationMap: Record<string, ConversationEntry[]>;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();

  // ─── Filters ───
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<EmployerFilters>(() => {
    const seasonParam = searchParams.get("season") ?? "";
    const seasonGroupParam = searchParams.get("seasonGroup") ?? "";
    // Map any season param to a broad group for filtering
    const SEASON_TO_GROUP: Record<string, string> = {
      winter: "snow", summer: "sun", festival: "festival",
      snow: "snow", sun: "sun",
    };
    // Also map region-specific SEASON_TYPES IDs to their display group
    for (const st of SEASON_TYPES) {
      SEASON_TO_GROUP[st.id] = st.displayGroup === "snow" ? "snow" : st.displayGroup === "festival" ? "festival" : "sun";
    }
    const resolvedGroup = seasonGroupParam || SEASON_TO_GROUP[seasonParam] || "";

    const roleParam = searchParams.get("role");
    const locationParam = searchParams.get("location");

    return {
      seasonType: "",
      seasonGroup: resolvedGroup,
      roles: new Set(roleParam ? [roleParam] : []),
      locations: new Set(locationParam ? [locationParam] : []),
      activeThisMonth: false,
    };
  });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // ─── Message modal ───
  const [messageTarget, setMessageTarget] = useState<BrowseCompany | null>(
    null
  );
  const [selectedWindowId, setSelectedWindowId] = useState("");
  const [messageContent, setMessageContent] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  function closeModal() {
    setMessageTarget(null);
    setSelectedWindowId("");
    setMessageContent("");
    setSendError(null);
    setSent(false);
  }

  // Focus trap + Escape key for message modal
  useEffect(() => {
    if (!messageTarget) return;
    function handleKeyDown(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") {
        closeModal();
        return;
      }
      if (e.key !== "Tab" || !modalRef.current) return;
      const focusable = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    const timer = setTimeout(() => {
      modalRef.current
        ?.querySelector<HTMLElement>("button, textarea")
        ?.focus();
    }, 50);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      clearTimeout(timer);
    };
  }, [messageTarget]);

  // ─── Filtered companies ───
  const filtered = useMemo(() => {
    return companies.filter((c) => {
      if (filters.activeThisMonth && !c.activeThisMonth) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const nameMatch = c.name.toLowerCase().includes(q);
        const descMatch = c.description?.toLowerCase().includes(q);
        const locationMatch = c.location?.toLowerCase().includes(q);
        if (!nameMatch && !descMatch && !locationMatch) return false;
      }
      // Effective season types: stored value, falling back to role-based inference
      const effectiveSeasons = (c.hiring_season_types?.length ?? 0) > 0
        ? c.hiring_season_types!
        : inferSeasonTypes(c.hiring_roles);
      // Season type (specific)
      if (filters.seasonType) {
        if (!effectiveSeasons.includes(filters.seasonType)) return false;
      }
      // Season group (broad)
      if (filters.seasonGroup) {
        const groupIds = SEASON_GROUP_IDS[filters.seasonGroup];
        if (!groupIds || !effectiveSeasons.some((t) => groupIds.includes(t))) return false;
      }
      // Roles
      if (filters.roles.size > 0) {
        if (!(c.hiring_roles ?? []).some((r) => filters.roles.has(r))) return false;
      }
      // Locations
      if (filters.locations.size > 0) {
        if (!(c.hiring_regions ?? []).some((l) => filters.locations.has(l))) return false;
      }
      return true;
    });
  }, [companies, searchQuery, filters]);

  const totalCompanies = companies.length;
  const hasAnyFilter = !!(
    searchQuery ||
    filters.seasonType ||
    filters.seasonGroup ||
    filters.roles.size > 0 ||
    filters.locations.size > 0 ||
    filters.activeThisMonth
  );

  // ─── Message modal helpers ───
  function openMessageModal(company: BrowseCompany) {
    setMessageTarget(company);
    setSelectedWindowId("");
    setMessageContent("");
    setSendError(null);
    setSent(false);
  }

  async function handleSendMessage() {
    if (!messageTarget || !selectedWindowId || !messageContent.trim()) return;
    setSending(true);
    setSendError(null);

    const result = await sendFirstMessageToEmployer(
      messageTarget.id,
      messageContent.trim(),
      selectedWindowId
    );

    if (result.error) {
      if (result.conversationId) {
        router.push(`/inbox/${result.conversationId}`);
        closeModal();
      } else {
        setSendError(result.error);
      }
    } else {
      setSent(true);
    }
    setSending(false);
  }

  function getExistingConversations(companyId: string): ConversationEntry[] {
    return conversationMap[companyId] ?? [];
  }

  const filterCount =
    (filters.seasonType || filters.seasonGroup ? 1 : 0) +
    (filters.roles.size > 0 ? 1 : 0) +
    (filters.locations.size > 0 ? 1 : 0) +
    (filters.activeThisMonth ? 1 : 0);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-[280px_minmax(0,1fr)] min-h-screen -mt-16 pt-16">
        {/* Desktop Sidebar */}
        <aside className="hidden md:block bg-white border-r border-border-light p-7 sticky top-16 h-[calc(100vh-64px)] overflow-y-auto">
          <FilterSidebar
            filters={filters}
            setFilters={setFilters}
            totalCount={totalCompanies}
            filteredCount={filtered.length}
          />
        </aside>

        {/* Mobile Filter Toggle */}
        <div className="md:hidden sticky top-16 z-50 bg-warm-bg/95 backdrop-blur-sm border-b border-border-light px-5 py-3">
          <div className="flex justify-between items-center mb-2.5">
            <span className="text-sm text-text-muted">
              <strong className="text-charcoal">{filtered.length}</strong>{" "}
              compan{filtered.length !== 1 ? "ies" : "y"}
              {hasAnyFilter ? " matching" : ""}
            </span>
          </div>
          <button
            onClick={() => setMobileFiltersOpen(true)}
            className="w-full py-2.5 rounded-lg text-[14px] font-semibold bg-gold text-white border-none cursor-pointer font-sans transition-all hover:bg-gold-light flex items-center justify-center gap-2 shadow-[0_2px_8px_rgba(28, 138, 153,0.25)]"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <line x1="1" y1="3" x2="13" y2="3" />
              <line x1="3" y1="7" x2="11" y2="7" />
              <line x1="5" y1="11" x2="9" y2="11" />
            </svg>
            {filterCount > 0 ? `Filters (${filterCount})` : "Filters"}
          </button>
          {/* Quick season chips */}
          <div className="flex gap-2 mt-2.5">
            {SEASON_GROUP_CHIPS.map((opt) => {
              const isActive = filters.seasonGroup === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() =>
                    setFilters((f) => {
                      const newGroup = isActive ? "" : opt.id;
                      const newLocsTree = newGroup
                        ? (getLocationsForSeasonGroup(newGroup) ?? LOCATIONS_TREE)
                        : LOCATIONS_TREE;
                      const allowedLocs = new Set(getAllLeafIdsFromTree(newLocsTree));
                      return {
                        ...f,
                        seasonGroup: newGroup,
                        seasonType: "",
                        locations: new Set([...f.locations].filter((id) => allowedLocs.has(id))),
                      };
                    })
                  }
                  className={`flex-1 py-1.5 rounded-full text-[12px] font-medium cursor-pointer font-sans transition-all ${
                    isActive
                      ? "bg-gold text-white border border-gold"
                      : "bg-white border border-border-light text-text-body hover:border-gold hover:text-gold active:bg-gold/5"
                  }`}
                >
                  {opt.icon} {opt.label}
                </button>
              );
            })}
            <button
              onClick={() => setFilters((f) => ({ ...f, activeThisMonth: !f.activeThisMonth }))}
              className={`flex-1 py-1.5 rounded-full text-[12px] font-medium cursor-pointer font-sans transition-all ${
                filters.activeThisMonth
                  ? "bg-gold text-white border border-gold"
                  : "bg-white border border-border-light text-text-body hover:border-gold hover:text-gold active:bg-gold/5"
              }`}
            >
              Active this month
            </button>
          </div>
        </div>

        {/* Mobile Filter Panel */}
        {mobileFiltersOpen && (
          <div className="fixed inset-0 z-[100] md:hidden">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setMobileFiltersOpen(false)}
            />
            <div className="absolute top-0 right-0 bottom-0 w-[85vw] max-w-[360px] bg-white p-5 overflow-y-auto animate-fade-in">
              <FilterSidebar
                filters={filters}
                setFilters={setFilters}
                totalCount={totalCompanies}
                filteredCount={filtered.length}
                onClose={() => setMobileFiltersOpen(false)}
              />
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="p-5 md:px-9 md:py-7">
          {/* Header */}
          <div className="mb-5">
            <h1 className="font-serif text-[28px] sm:text-[36px] text-navy leading-tight mb-1">
              Find Employers
            </h1>
            <p className="text-[14px] sm:text-[15px] text-text-muted max-w-[480px] leading-relaxed">
              Connect with top employers looking for seasonal staff.
            </p>
          </div>

          {/* Search bar */}
          <div className="mb-5">
            <div className="relative">
              <svg
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted/50"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search companies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-[10px] border-[1.5px] border-border bg-white text-sm font-sans text-charcoal outline-none focus:border-gold transition-colors placeholder:text-text-muted/60"
              />
            </div>
          </div>

          {/* Desktop season chips */}
          <div className="hidden md:flex gap-2 mb-4">
            {SEASON_GROUP_CHIPS.map((opt) => {
              const isActive = filters.seasonGroup === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() =>
                    setFilters((f) => {
                      const newGroup = isActive ? "" : opt.id;
                      const newLocsTree = newGroup
                        ? (getLocationsForSeasonGroup(newGroup) ?? LOCATIONS_TREE)
                        : LOCATIONS_TREE;
                      const allowedLocs = new Set(getAllLeafIdsFromTree(newLocsTree));
                      return {
                        ...f,
                        seasonGroup: newGroup,
                        seasonType: "",
                        locations: new Set([...f.locations].filter((id) => allowedLocs.has(id))),
                      };
                    })
                  }
                  className={`py-1.5 px-4 rounded-full text-[12px] font-medium cursor-pointer font-sans transition-all ${
                    isActive
                      ? "bg-gold text-white border border-gold"
                      : "bg-white border border-border-light text-text-body hover:border-gold hover:text-gold active:bg-gold/5"
                  }`}
                >
                  {opt.icon} {opt.label}
                </button>
              );
            })}
            <button
              onClick={() => setFilters((f) => ({ ...f, activeThisMonth: !f.activeThisMonth }))}
              className={`py-1.5 px-4 rounded-full text-[12px] font-medium cursor-pointer font-sans transition-all ${
                filters.activeThisMonth
                  ? "bg-gold text-white border border-gold"
                  : "bg-white border border-border-light text-text-body hover:border-gold hover:text-gold active:bg-gold/5"
              }`}
            >
              Active this month
            </button>
          </div>

          {/* Results count + clear (desktop) */}
          {hasAnyFilter && (
            <div className="hidden md:flex items-center gap-3 mb-4">
              <span className="text-[13px] font-semibold text-charcoal">
                {filtered.length} compan{filtered.length !== 1 ? "ies" : "y"}{" "}
                found
              </span>
              <button
                onClick={() => {
                  setSearchQuery("");
                  setFilters({ seasonType: "", seasonGroup: "", roles: new Set(), locations: new Set(), activeThisMonth: false });
                }}
                className="text-[12px] font-medium text-gold bg-transparent border-none cursor-pointer font-sans hover:text-gold-light transition-colors"
              >
                Clear filters
              </button>
            </div>
          )}

          {/* Results */}
          {filtered.length === 0 ? (
            <div className="text-center py-16">
              {companies.length === 0 ? (
                <>
                  <p className="text-lg text-text-muted mb-2">
                    No employers have joined yet.
                  </p>
                  <p className="text-sm text-text-muted">
                    Check back soon! Employers are joining PeakWave every week.
                  </p>
                </>
              ) : (
                <>
                  <div className="text-[32px] mb-3">&#128269;</div>
                  <p className="text-base font-semibold text-text-body mb-1">
                    No employers match your filters
                  </p>
                  <p className="text-sm text-text-muted">
                    Try broadening your search or removing filters.
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5">
              {filtered.map((company) => (
                <EmployerCard
                  key={company.id}
                  company={company}
                  userRole={userRole}
                  candidateProfile={candidateProfile}
                  existingConversations={getExistingConversations(company.id)}
                  onMessage={() => openMessageModal(company)}
                />
              ))}
            </div>
          )}

          {/* Suggestion prompt */}
          <div className="mt-10 px-4 py-3 rounded-[10px] bg-gold/[0.04] border border-gold/15 flex items-center gap-2.5 text-[13px] text-text-muted">
            <span className="text-base shrink-0" aria-hidden="true">
              &#128161;
            </span>
            <span>
              Know a great seasonal employer?{" "}
              <a
                href="mailto:hello@peakwave.co"
                className="text-gold font-semibold no-underline hover:text-gold-light transition-colors"
              >
                Tell us about them
              </a>
            </span>
          </div>
        </main>
      </div>

      {/* ─── Message Modal ─── */}
      {messageTarget && (
        <div
          className="fixed inset-0 z-[2000] bg-black/40 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-label={`Message ${messageTarget.name}`}
            className="bg-white rounded-[20px] w-full max-w-[480px] max-h-[90vh] overflow-y-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]"
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-serif text-xl text-navy">
                  Message {messageTarget.name}
                </h2>
                <button
                  onClick={closeModal}
                  className="w-8 h-8 rounded-full bg-warm-bg flex items-center justify-center border-none cursor-pointer hover:bg-border transition-colors"
                  aria-label="Close"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    stroke="#7A7A7A"
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <line x1="1" y1="1" x2="13" y2="13" />
                    <line x1="13" y1="1" x2="1" y2="13" />
                  </svg>
                </button>
              </div>

              {sent ? (
                <div className="text-center py-6">
                  <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#22C55E"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <p className="text-[15px] font-semibold text-charcoal mb-1">
                    Message sent
                  </p>
                  <p className="text-[13px] text-text-muted mb-4">
                    {messageTarget.name} will see your message in their inbox.
                  </p>
                  <button
                    onClick={closeModal}
                    className="px-6 py-2.5 rounded-sm text-[13px] font-semibold bg-gold text-white border-none cursor-pointer font-sans hover:bg-gold-light transition-all"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <>
                  {/* Select availability window */}
                  {candidateProfile &&
                    candidateProfile.availabilityWindows.length > 0 && (
                      <div className="mb-4">
                        <label className="text-xs font-semibold uppercase tracking-[0.8px] text-charcoal mb-2 block">
                          Which season are you enquiring about?
                        </label>
                        <div className="flex flex-col gap-2">
                          {candidateProfile.availabilityWindows.map((w) => {
                            const hasConv = (
                              conversationMap[messageTarget.id] ?? []
                            ).some((c) => c.windowId === w.id);
                            return (
                              <button
                                key={w.id}
                                type="button"
                                disabled={hasConv}
                                onClick={() => setSelectedWindowId(w.id)}
                                className={`px-4 py-3 rounded-[10px] text-left text-sm font-sans border-[1.5px] transition-all cursor-pointer ${
                                  hasConv
                                    ? "opacity-50 cursor-not-allowed border-border bg-warm-bg text-text-muted"
                                    : selectedWindowId === w.id
                                    ? "border-gold bg-gold/5 text-charcoal"
                                    : "border-border bg-warm-bg text-charcoal hover:border-gold/50"
                                }`}
                              >
                                <span className="font-medium">
                                  {w.season_label}
                                </span>
                                {hasConv && (
                                  <span className="text-[11px] text-text-muted ml-2">
                                    (already messaged)
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  {candidateProfile &&
                    candidateProfile.availabilityWindows.length === 0 && (
                      <div className="bg-warm-bg rounded-[10px] px-4 py-3 mb-4">
                        <p className="text-[13px] text-text-muted">
                          You need at least one active availability window to
                          message employers.{" "}
                          <Link
                            href="/profile?setup=work#availability"
                            className="text-gold font-medium no-underline hover:text-gold-light"
                          >
                            Set up your work profile
                          </Link>
                        </p>
                      </div>
                    )}

                  {/* Message input */}
                  <div className="mb-4">
                    <label className="text-xs font-semibold uppercase tracking-[0.8px] text-charcoal mb-2 block">
                      Your message
                    </label>
                    <textarea
                      value={messageContent}
                      onChange={(e) => setMessageContent(e.target.value)}
                      rows={4}
                      placeholder={`Introduce yourself to ${messageTarget.name}...`}
                      className="w-full px-4 py-3 border-[1.5px] border-border rounded-[10px] text-sm font-sans text-charcoal bg-warm-bg resize-y outline-none focus:border-gold focus:bg-white placeholder:text-[#B0A99F] transition-all"
                      maxLength={1000}
                    />
                    <p className="text-[11px] text-text-muted text-right mt-1">
                      {messageContent.length}/1000
                    </p>
                  </div>

                  {/* Error */}
                  {sendError && (
                    <div className="bg-error/10 border border-error/30 text-error rounded-[10px] px-4 py-2.5 mb-4 text-[13px] font-medium">
                      {sendError}
                    </div>
                  )}

                  {/* Send button */}
                  <button
                    onClick={handleSendMessage}
                    disabled={
                      sending ||
                      !selectedWindowId ||
                      !messageContent.trim() ||
                      !candidateProfile
                    }
                    className="w-full px-6 py-3 rounded-sm text-[14px] font-semibold bg-gold text-white border-none cursor-pointer font-sans transition-all hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sending ? "Sending..." : "Send Message"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ═══ EMPLOYER CARD ═══

function EmployerCard({
  company,
  userRole,
  candidateProfile,
  existingConversations,
  onMessage,
}: {
  company: BrowseCompany;
  userRole?: UserRole;
  candidateProfile: CandidateProfile | null;
  existingConversations: ConversationEntry[];
  onMessage: () => void;
}) {
  const router = useRouter();

  const effectiveSeasons = (company.hiring_season_types?.length ?? 0) > 0
    ? company.hiring_season_types
    : inferSeasonTypes(company.hiring_roles);
  const seasonLabels = getSeasonDisplayLabels(effectiveSeasons);
  const defaultAccent = seasonLabels.length > 0 ? seasonLabels[0].bg : "#1B2840";
  const accentColor = company.brand_color || defaultAccent;
  const initials = company.name
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const allRoleLabels = (company.hiring_roles ?? []).map(
    (id) => findLabelById(ROLES_TREE, id) ?? id
  );
  const roleLabels = allRoleLabels.slice(0, 4);
  const extraRoles = Math.max(0, allRoleLabels.length - 4);

  // Message button state
  const hasActiveWindows =
    (candidateProfile?.availabilityWindows.length ?? 0) > 0;
  const allWindowsMessaged =
    candidateProfile &&
    hasActiveWindows &&
    candidateProfile.availabilityWindows.every((w) =>
      existingConversations.some((c) => c.windowId === w.id)
    );
  const hasConversation = existingConversations.length > 0;
  const canMessage =
    userRole === "candidate" &&
    hasActiveWindows &&
    !allWindowsMessaged &&
    company.allow_inbound_messages;
  const showDisabledMessaging =
    !company.allow_inbound_messages &&
    userRole === "candidate" &&
    !hasConversation;

  const isVerifiedHiring = company.verified && company.activeListingCount > 0;

  const profileHref = company.slug ? `/company/${company.slug}` : null;

  function handleSecondaryClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (canMessage) {
      onMessage();
    } else if (!userRole) {
      router.push(buildWorkSignupHref({ next: "/jobs", source: "employers" }));
    }
  }

  // Secondary CTA text
  let secondaryLabel: string | null = null;
  const secondaryDisabled = false;
  let secondaryTitle: string | undefined;

  if (showDisabledMessaging) {
    secondaryLabel = null; // don't show
  } else if (canMessage) {
    secondaryLabel = "Message";
  } else if (
    userRole === "candidate" &&
    hasConversation &&
    !canMessage &&
    hasActiveWindows
  ) {
    secondaryLabel = null; // already messaged, don't clutter
  } else if (userRole === "candidate" && !hasActiveWindows) {
    secondaryLabel = null;
  } else if (userRole === "employer") {
    secondaryLabel = null;
  } else if (!userRole) {
    secondaryLabel = "Sign up to message";
  }

  const cardContent = (
    <>
      <div className="p-5 sm:p-6 flex-1 flex flex-col">
        {/* Top row: avatar + name on left, badges on right */}
        <div className="flex items-center gap-3.5">
          {/* Logo or initials */}
          {company.logo_url ? (
            <Image
              src={company.logo_url}
              alt={`${company.name} logo`}
              width={48}
              height={48}
              sizes="48px"
              className="w-12 h-12 rounded-[12px] object-cover shrink-0"
            />
          ) : (
            <div
              className="w-12 h-12 rounded-[12px] flex items-center justify-center text-white text-[16px] font-bold shrink-0"
              style={{
                background: `linear-gradient(135deg, ${accentColor}, ${accentColor}dd)`,
              }}
            >
              {initials}
            </div>
          )}

          <div className="flex-1 min-w-0">
            <h3 className="font-serif text-[16px] font-bold text-navy m-0 truncate">
              {company.name}
            </h3>
            {company.location && (
              <p className="text-[13px] text-text-muted mt-0.5 m-0 truncate">
                {company.location}
              </p>
            )}
          </div>

          {(company.verified || company.activeListingCount > 0) && (
            <div className="flex items-center gap-1.5 shrink-0">
              {company.verified && <VerifiedBadge />}
              {company.activeListingCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#ECFDF5] text-[#047857] text-[11px] font-semibold tracking-[0.2px]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
                  {company.activeListingCount} open role{company.activeListingCount !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Season type tags */}
        {seasonLabels.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {seasonLabels.map((s) => (
              <span
                key={s.label}
                className="inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold"
                style={{
                  backgroundColor: s.tint,
                  color: s.bg,
                }}
              >
                {s.label}
              </span>
            ))}
          </div>
        )}

        {/* Role pills */}
        {roleLabels.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {roleLabels.map((r) => (
              <span
                key={r}
                className="px-2.5 py-1 rounded-sm bg-[#EEF2F7] text-[#4A5568] text-[11px] font-medium"
              >
                {r}
              </span>
            ))}
            {extraRoles > 0 && (
              <span className="px-2.5 py-1 rounded-sm bg-[#EEF2F7] text-text-muted text-[11px] font-medium">
                +{extraRoles} more
              </span>
            )}
          </div>
        )}

        {/* Location pills */}
        {(company.hiring_regions?.length ?? 0) > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="shrink-0 text-text-muted"
            >
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            {(company.hiring_regions ?? []).slice(0, 3).map((id) => (
              <span
                key={id}
                className="px-2 py-0.5 rounded-sm bg-[#F0EDE7] text-[#6B6355] text-[11px] font-medium"
              >
                {findLabelById(LOCATIONS_TREE, id) ?? id}
              </span>
            ))}
            {(company.hiring_regions?.length ?? 0) > 3 && (
              <span className="px-2 py-0.5 rounded-sm bg-[#F0EDE7] text-text-muted text-[11px] font-medium">
                +{(company.hiring_regions?.length ?? 0) - 3} more
              </span>
            )}
          </div>
        )}

        {/* Response time */}
        {company.avg_response_hours != null &&
          company.avg_response_hours <= 72 && (
            <p className="mt-2.5 m-0 text-[11px] text-text-muted flex items-center gap-1">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0 text-[#D97706]"
              >
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
              </svg>
              {formatResponseTime(company.avg_response_hours)}
            </p>
          )}

        {/* Recency */}
        {company.recencyBucket && (
          <p className="mt-1.5 m-0 text-[11px] text-text-muted flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] shrink-0" />
            Active this {company.recencyBucket}
          </p>
        )}

        {/* Secondary CTA */}
        {secondaryLabel && (
          <div className="mt-auto pt-3 flex justify-end">
            <button
              onClick={handleSecondaryClick}
              disabled={secondaryDisabled}
              title={secondaryTitle}
              className="relative z-10 text-[12px] font-medium text-text-muted bg-transparent border-none cursor-pointer font-sans hover:text-gold transition-colors shrink-0"
              aria-label={
                secondaryDisabled
                  ? secondaryTitle || "Messaging unavailable"
                  : `${secondaryLabel} ${company.name}`
              }
            >
              {secondaryLabel}
            </button>
          </div>
        )}
      </div>
    </>
  );

  const cardClass = `bg-white rounded-[18px] border overflow-hidden transition-all duration-200 flex flex-col cursor-pointer no-underline text-inherit ${
    isVerifiedHiring
      ? "border-gold/20 shadow-[0_2px_8px_rgba(28, 138, 153,0.08)] hover:border-gold/40"
      : "border-border-light shadow-sm hover:border-gold/40"
  } hover:shadow-[0_12px_40px_rgba(0,0,0,0.08)] hover:-translate-y-1`;

  if (profileHref) {
    return (
      <Link href={profileHref} className={cardClass}>
        {cardContent}
      </Link>
    );
  }

  return <div className={cardClass}>{cardContent}</div>;
}
